/* global indexedDB, IDBDatabase */
/**
 * Browser-local IndexedDB cache for uploaded TrackPoint database files.
 * All data stays in the browser — nothing is sent to the server.
 */

const DB_NAME = 'trackpoint-cache'
const STORE_NAME = 'databases'
const DB_VERSION = 1
const MAX_ENTRIES = 10

export type CachedDbMeta = {
  id: string
  fileName: string
  fileSize: number
  uploadedAt: number
}

export type CachedDbEntry = CachedDbMeta & {
  buffer: ArrayBuffer
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function generateId(): string {
  const ts = Date.now()
  const rand = Math.random().toString(36).slice(2, 5)
  return `${ts}-${rand}`
}

function getAllEntries(db: IDBDatabase): Promise<CachedDbEntry[]> {
  const tx = db.transaction(STORE_NAME, 'readonly')
  const req = tx.objectStore(STORE_NAME).getAll()
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as CachedDbEntry[])
    req.onerror = () => reject(req.error)
  })
}

/** List all cached DBs (without buffer), sorted by uploadedAt descending. */
export async function listDBs(): Promise<CachedDbMeta[]> {
  const db = await openDB()
  const entries = await getAllEntries(db)
  db.close()
  const metas = entries.map(({ buffer: _, ...meta }) => meta)
  metas.sort((a, b) => b.uploadedAt - a.uploadedAt)
  return metas
}

/** Save a file to cache. Returns the generated id. Evicts oldest if over limit. */
export async function saveDB(fileName: string, buffer: ArrayBuffer): Promise<string> {
  const db = await openDB()
  const id = generateId()
  const entry: CachedDbEntry = {
    id,
    fileName,
    fileSize: buffer.byteLength,
    uploadedAt: Date.now(),
    buffer,
  }

  // Get all entries to check count and find eviction candidates
  const all = await getAllEntries(db)
  all.sort((a, b) => b.uploadedAt - a.uploadedAt)
  const toEvict = all.length >= MAX_ENTRIES ? all.slice(MAX_ENTRIES - 1) : []

  const tx = db.transaction(STORE_NAME, 'readwrite')
  const store = tx.objectStore(STORE_NAME)
  for (const old of toEvict) {
    store.delete(old.id)
  }
  store.put(entry)

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve(id)
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

/** Load a cached DB entry by id (including buffer). Returns null if not found. */
export async function loadDB(id: string): Promise<CachedDbEntry | null> {
  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readonly')
  const req = tx.objectStore(STORE_NAME).get(id)

  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      db.close()
      resolve((req.result as CachedDbEntry | undefined) ?? null)
    }
    req.onerror = () => {
      db.close()
      reject(req.error)
    }
  })
}

/** Delete a cached DB entry by id. */
export async function deleteDB(id: string): Promise<void> {
  const db = await openDB()
  const tx = db.transaction(STORE_NAME, 'readwrite')
  tx.objectStore(STORE_NAME).delete(id)

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => {
      db.close()
      resolve()
    }
    tx.onerror = () => {
      db.close()
      reject(tx.error)
    }
  })
}

/** Build the URL search param value for a cached DB: "<id>-<fileName>" */
export function toDbParam(id: string, fileName: string): string {
  return `${id}-${fileName}`
}

/** Parse the db search param back to { id, fileName }. Returns null if invalid. */
export function parseDbParam(param: string): { id: string; fileName: string } | null {
  // id format: "<timestamp>-<3chars>", e.g. "1718234567890-x3f"
  const match = param.match(/^(\d+-[a-z0-9]{3})-(.+)$/)
  if (!match) return null
  return { id: match[1]!, fileName: match[2]! }
}

/** Format bytes to human-readable string. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}
